import React from "react";
import { useTranslation } from "react-i18next";

// Status line under a list's header row (see useListSearchFeedback). Pure
// presentation: everything it says comes from state the list already holds -
// 0 calls. Which list is speaking is decided by `texts` (the seven
// translation keys), so the logo list ("N logos match") and the design list
// ("N designs match") share one component. Generic twin of the logo list's
// LogoSearchStatus (#806), which is left untouched.
export interface ListSearchStatusTexts {
  // "Searching for '{{term}}'…"
  searching: string;
  // "Loading all designs…" (clearing the search)
  loadingAll: string;
  // "{{n}} designs match '{{term}}'"
  matchCount: string;
  // "1 design matches '{{term}}'"
  matchOne: string;
  // "{{n}} designs match '{{term}}' on this page" (no totalCount)
  matchCountOnPage: string;
  // "No designs match '{{term}}'. Try another word or clear the search."
  noMatch: string;
  // "Clear search"
  clear: string;
}

// The seven keys of one translation namespace, e.g. listSearchTexts(
// "designList") -> designList.searchSearching … designList.searchClear.
export function listSearchTexts(namespace: string): ListSearchStatusTexts {
  return {
    searching: `${namespace}.searchSearching`,
    loadingAll: `${namespace}.searchLoadingAll`,
    matchCount: `${namespace}.searchMatchCount`,
    matchOne: `${namespace}.searchMatchOne`,
    matchCountOnPage: `${namespace}.searchMatchCountOnPage`,
    noMatch: `${namespace}.searchNoMatch`,
    clear: `${namespace}.searchClear`,
  };
}

export interface ListSearchStatusProps {
  // Term on screen: the draft while the user is still typing, otherwise the
  // query's search term.
  term: string;
  // Typing (debounce window) or a search-driven fetch in flight.
  isSearching: boolean;
  // List-search throttle (flag listSearchThrottleEnabled): the typed term is
  // under this minimum, so no search is coming for it - say so instead of
  // "Searching…" (compGenericTable.searchMinChars, shared by every list).
  // Undefined (throttle off, or term long enough) = as before.
  minCharsHint?: number;
  // The list call's totalCount; undefined = older shape -> "… on this page".
  totalCount?: number;
  // Rows on the current page (the fallback count).
  pageResultCount: number;
  onClear: () => void;
  texts: ListSearchStatusTexts;
  testId?: string;
}

export function ListSearchStatus({
  term,
  isSearching,
  minCharsHint,
  totalCount,
  pageResultCount,
  onClear,
  texts,
  testId = "list-search-status",
}: ListSearchStatusProps) {
  const { t } = useTranslation();
  const shownTerm = term.trim();
  let text: string | null = null;
  let showClear = false;
  if (minCharsHint !== undefined) {
    text = t("compGenericTable.searchMinChars", { n: minCharsHint });
  } else if (isSearching) {
    text = shownTerm
      ? t(texts.searching, { term: shownTerm })
      : t(texts.loadingAll);
  } else if (shownTerm) {
    const n = totalCount ?? pageResultCount;
    if (n === 0) {
      text = t(texts.noMatch, { term: shownTerm });
      showClear = true;
    } else if (totalCount === undefined) {
      text = t(texts.matchCountOnPage, { n, term: shownTerm });
    } else if (n === 1) {
      text = t(texts.matchOne, { term: shownTerm });
    } else {
      text = t(texts.matchCount, { n, term: shownTerm });
    }
  }
  // The live region stays mounted while the flag is on (a region inserted
  // together with its first text is not reliably announced); with nothing
  // to say it has no content, no padding and no height.
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      className={
        text
          ? "px-8 -mt-2 pb-3 text-xs text-gray-600 flex items-center gap-x-3"
          : undefined
      }
    >
      {text && <span>{text}</span>}
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          className="px-3 py-1 rounded-md border border-borderGray bg-white font-semibold text-primary-700 hover:bg-gray-100 whitespace-nowrap"
        >
          {t(texts.clear)}
        </button>
      )}
    </div>
  );
}
